import DocumentObjectTypeIcon from "Areas/SharedFeatures/documentObjectTypeIcon";
import { documentObjectIconMap } from "Common/dictionaries/documentObjectIconId";
import { OPTION_TYPE } from "Common/dictionaries/optionType";
import { SORT_ORDER } from "Common/dictionaries/sortOrder";
import DataGridBase, { DateCell } from "Components/dataGridBase";
import { ImageWidget } from "Components/optionForm";
import { Column, ColumnChooser } from "devextreme-react/data-grid";
import compact from "lodash/compact";
import difference from "lodash/difference";
import filter from "lodash/filter";
import find from "lodash/find";
import forEach from "lodash/forEach";
import head from "lodash/head";
import includes from "lodash/includes";
import intersection from "lodash/intersection";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import join from "lodash/join";
import map from "lodash/map";
import reject from "lodash/reject";
import uniq from "lodash/uniq";
import values from "lodash/values";
import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { v4 as uuid } from "uuid";
import { optionTypeAllowHeaderFilteringMap, optionTypeDataTypeMap } from "../utils/utils";
import locale from "./table.inner.locale";
import isNumber from "lodash/isNumber";
const htmlIconElementMap = {};
const visibleColumnRegexp = /columns\[(?<ind>\d+)\]\.visible/;
const sortOrderColumnRegexp = /columns\[(?<ind>\d+)\]\.sortOrder/;
const columnChooserSearch = { enabled: false };
const SIZES = {
    DOCUMENT_OBJECT_TYPES_PANEL_HEIGHT: 40
};
function TableInner(props) {
    var _a, _b, _c;
    const initialGridRef = useRef(null);
    const gridRef = props.gridRef || initialGridRef;
    const gridDataRef = useRef();
    const columnKeyToAttributeIdMapRef = useRef({});
    const [filterDocumentObjectIds, setFilterDocumentObjectIds] = useState(undefined);
    useEffect(() => {
        if (props.tableRef) {
            props.tableRef.current = {
                getColumnIndex: (rowId, fieldId) => {
                    var _a;
                    return columnKeyToAttributeIdMapRef.current[rowId]
                        ? (_a = find(columnKeyToAttributeIdMapRef.current[rowId], ["attributeId", fieldId])) === null || _a === void 0 ? void 0 : _a.columnIndex
                        : undefined;
                }
            };
        }
    }, [props.tableRef]);
    useEffect(() => {
        var _a;
        if (props.navigateToId) {
            const instance = (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance;
            if (instance) {
                instance.selectRows([props.navigateToId], false);
                instance.navigateToRow(props.navigateToId);
            }
            props.onResetNavigateTo && props.onResetNavigateTo();
        }
    }, [props.navigateToId]);
    const tableId = useMemo(() => uuid(), [props.documentId, props.columns]);
    useLayoutEffect(() => {
        setFilterDocumentObjectIds(undefined);
    }, [tableId]);
    useEffect(() => {
        var _a;
        if (!((_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance) || !props.settings)
            return;
        const prevElementAttr = gridRef.current.instance.option("elementAttr") || {};
        gridRef.current.instance.option("elementAttr", {
            ...prevElementAttr,
            class: join(compact([props.settings.fontSize, props.settings.alignment]), " ")
        });
    }, [(_a = props.settings) === null || _a === void 0 ? void 0 : _a.fontSize, (_b = props.settings) === null || _b === void 0 ? void 0 : _b.alignment]);
    const optionTypeCellRenderMap = useMemo(() => ({
        [OPTION_TYPE.DATETIME]: DateCell,
        [OPTION_TYPE.IMAGE]: ({ data, column }) => {
            var _a;
            const defaultValue = data.DocumentObjectIconId
                ? documentObjectIconMap[data.DocumentObjectIconId]
                : undefined;
            const attributeId = data.NodeId &&
                column.dataField &&
                columnKeyToAttributeIdMapRef.current &&
                columnKeyToAttributeIdMapRef.current[data.NodeId]
                ? (_a = columnKeyToAttributeIdMapRef.current[data.NodeId][column.dataField]) === null || _a === void 0 ? void 0 : _a.attributeId
                : undefined;
            const largeAttributeId = attributeId && data.LargeAttributeValues && data.LargeAttributeValues[attributeId]
                ? head(data.LargeAttributeValues[attributeId])
                : undefined;
            const actualData = find(gridDataRef.current, ["Id", data.Id]);
            return (React.createElement(ImageWidget, { className: "pl-xxs pr-xxs d-flex v-center-items", key: column.dataField || "", readOnly: true, value: actualData && column.dataField
                    ? actualData[column.dataField]
                    : undefined, defaultValue: defaultValue, size: props.imageWidgetSize, onGetPreview: props.documentId && data.NodeId && attributeId && largeAttributeId
                    ? (completed, rejected) => {
                        if (!(props.documentId && data.NodeId && props.onGetPreview)) {
                            completed("");
                            return;
                        }
                        props.onGetPreview(props.documentId, data.NodeId, attributeId, largeAttributeId, completed, rejected);
                    }
                    : undefined, zIndex: props.zIndex }));
        }
    }), [props.onGetPreview, props.documentId, props.zIndex]);
    const columns = useMemo(() => {
        var _a, _b;
        return [
            React.createElement(Column, { key: "_objectType", dataField: "DocumentObjectName", caption: locale.OBJECT_TYPE, dataType: "string", minWidth: 170, sortOrder: props.tableSortOptions === undefined ||
                    ((_a = props.tableSortOptions) === null || _a === void 0 ? void 0 : _a.dataField) === "DocumentObjectName"
                    ? ((_b = props.tableSortOptions) === null || _b === void 0 ? void 0 : _b.sortOrder) || SORT_ORDER.ASC
                    : undefined, width: "auto", visible: true, showInColumnChooser: false }),
            ...map(props.columns, col => {
                var _a, _b, _c;
                return (React.createElement(Column, { key: col.Key, dataField: col.Key, caption: col.Name, cellRender: optionTypeCellRenderMap[col.Type], dataType: optionTypeDataTypeMap[col.Type] || "string", allowHeaderFiltering: optionTypeAllowHeaderFilteringMap[col.Type] || false, sortOrder: ((_a = props.tableSortOptions) === null || _a === void 0 ? void 0 : _a.dataField) === col.Key
                        ? (_b = props.tableSortOptions) === null || _b === void 0 ? void 0 : _b.sortOrder
                        : undefined, width: "auto", visible: Boolean(!includes((_c = props.settings) === null || _c === void 0 ? void 0 : _c.hiddenColumns, col.Key) &&
                        (!filterDocumentObjectIds ||
                            (col.DocumentObjectIds &&
                                difference(col.DocumentObjectIds, filterDocumentObjectIds).length <
                                    col.DocumentObjectIds.length))) }));
            }),
            React.createElement(ColumnChooser, { key: "_select", mode: "select", search: columnChooserSearch })
        ];
    }, [props.columns, filterDocumentObjectIds]);
    const filterBuilderFields = useMemo(() => [
        {
            dataField: "DocumentObjectName",
            caption: locale.OBJECT_TYPE,
            dataType: "string"
        },
        ...map(props.columns, col => ({
            dataField: col.Key,
            caption: col.Name,
            dataType: optionTypeDataTypeMap[col.Type] || "string"
        }))
    ], [props.columns, filterDocumentObjectIds]);
    const gridData = useMemo(() => {
        const result = props.data && props.columns && props.documentObjects
            ? map(props.data, n => {
                const documentObject = props.documentObjects && n.DocumentObjectId
                    ? props.documentObjects[n.DocumentObjectId]
                    : undefined;
                const attributeIds = map(documentObject === null || documentObject === void 0 ? void 0 : documentObject.Attributes, "Key");
                const item = {
                    Id: n.Id,
                    NodeId: n.DocumentObjectId ? n.Id : undefined,
                    LargeAttributeValues: n.LargeAttributeValues || {},
                    DocumentObjectId: n.DocumentObjectId,
                    DocumentObjectName: (documentObject === null || documentObject === void 0 ? void 0 : documentObject.Name) || "",
                    DocumentObjectIconId: (documentObject === null || documentObject === void 0 ? void 0 : documentObject.IconId) || ""
                };
                forEach(n.Values, (v, ind) => {
                    const column = props.columns ? props.columns[ind] : undefined;
                    if (!column)
                        return;
                    item[column.Key] = v;
                    if (attributeIds.length &&
                        (!columnKeyToAttributeIdMapRef.current[n.Id] ||
                            !columnKeyToAttributeIdMapRef.current[n.Id][column.Key])) {
                        const attrId = head(intersection(column.AttributeIds, attributeIds));
                        if (attrId) {
                            if (!columnKeyToAttributeIdMapRef.current[n.Id])
                                columnKeyToAttributeIdMapRef.current[n.Id] = {};
                            columnKeyToAttributeIdMapRef.current[n.Id][column.Key] = {
                                attributeId: attrId,
                                columnIndex: ind
                            };
                        }
                    }
                });
                return item;
            })
            : undefined;
        gridDataRef.current = result;
        return result;
    }, [props.data, props.documentObjects, props.columns, optionTypeCellRenderMap]);
    const handleCellPrepared = useCallback((e) => {
        if (e.column.dataField === "DocumentObjectName" &&
            e.rowType === "data" &&
            e.data &&
            e.data.DocumentObjectId) {
            const DocumentObjectId = parseInt(e.data.DocumentObjectId);
            if (isNumber(DocumentObjectId)) {
                if (!htmlIconElementMap[DocumentObjectId]) {
                    const htmlIcon = ReactDOMServer.renderToStaticMarkup(React.createElement(DocumentObjectTypeIcon, { className: "v-align-text-top mr-0_5", documentObjectIconId: e.data.DocumentObjectIconId, documentObjectName: e.data.DocumentObjectName, showEmptyBlockIfNoIcon: true }));
                    const parser = new DOMParser();
                    const parsedDocument = parser.parseFromString(htmlIcon, "text/html");
                    const htmlIconElement = parsedDocument.body.children[0];
                    htmlIconElementMap[DocumentObjectId] = htmlIconElement;
                }
                e.cellElement.prepend(htmlIconElementMap[DocumentObjectId].cloneNode());
            }
        }
    }, []);
    const handleSetSelected = useCallback((items) => {
        if (!props.documentId || !props.onChangeSelected)
            return;
        const nodeIds = compact(map(items, "NodeId"));
        const rowIds = compact(map(items, "Id"));
        props.onChangeSelected(props.documentId, nodeIds, rowIds);
    }, [props.documentId, props.onChangeSelected]);
    const handleOptionChanged = useCallback((e) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        if (e.fullName && visibleColumnRegexp.test(e.fullName)) {
            const index = parseInt(((_b = (_a = visibleColumnRegexp.exec(e.fullName)) === null || _a === void 0 ? void 0 : _a.groups) === null || _b === void 0 ? void 0 : _b.ind) || "");
            if (Number.isFinite(index) && ((_d = (_c = columns[index]) === null || _c === void 0 ? void 0 : _c.props) === null || _d === void 0 ? void 0 : _d.dataField)) {
                const dataField = columns[index].props.dataField;
                const columnSettings = find(props.columns, ["Key", dataField]);
                const visibleByFilterDocumentObjectIds = !filterDocumentObjectIds ||
                    (columnSettings &&
                        columnSettings.DocumentObjectIds &&
                        difference(columnSettings.DocumentObjectIds, filterDocumentObjectIds).length <
                            columnSettings.DocumentObjectIds.length);
                if (!visibleByFilterDocumentObjectIds && !e.value)
                    return;
                props.onSetHiddenColumns &&
                    props.onSetHiddenColumns(e.value
                        ? reject((_e = props.settings) === null || _e === void 0 ? void 0 : _e.hiddenColumns, col => col === dataField)
                        : uniq([...(((_f = props.settings) === null || _f === void 0 ? void 0 : _f.hiddenColumns) || []), dataField]));
            }
        }
        else if (e.fullName === "columns[0].filterValues") {
            if (isUndefined(e.value)) {
                setFilterDocumentObjectIds(undefined);
            }
            else {
                const ids = map(filter(props.documentObjects, item => includes(e.value, item.Name)), "Id");
                setFilterDocumentObjectIds(ids.length ? ids : undefined);
            }
        }
        else if (e.fullName && sortOrderColumnRegexp.test(e.fullName)) {
            const index = parseInt(((_h = (_g = sortOrderColumnRegexp.exec(e.fullName)) === null || _g === void 0 ? void 0 : _g.groups) === null || _h === void 0 ? void 0 : _h.ind) || "");
            if (Number.isFinite(index) && ((_k = (_j = columns[index]) === null || _j === void 0 ? void 0 : _j.props) === null || _k === void 0 ? void 0 : _k.dataField)) {
                const dataField = columns[index].props.dataField;
                props.documentId &&
                    props.onSetTableSortOptions &&
                    props.onSetTableSortOptions(props.documentId, dataField, e.value);
            }
        }
    }, [
        (_c = props.settings) === null || _c === void 0 ? void 0 : _c.hiddenColumns,
        props.onSetHiddenColumns,
        props.documentId,
        props.documentObjects,
        props.columns,
        props.onSetTableSortOptions,
        filterDocumentObjectIds
    ]);
    const handleFilterValueChange = useCallback((value) => {
        props.documentId &&
            props.onSetTableFilters &&
            props.onSetTableFilters(props.documentId, value ? JSON.stringify(value) : value);
    }, [props.documentId, props.onSetTableFilters]);
    const handleInitialized = useCallback((e) => {
        var _a;
        if (!e.component)
            return;
        if (props.tableFilters) {
            try {
                const filterValue = JSON.parse(props.tableFilters);
                if (!e.component || !((_a = e.element) === null || _a === void 0 ? void 0 : _a.isConnected))
                    return;
                e.component.option("filterValue", filterValue);
            }
            catch (ex) {
                console.warn(props.tableFilters, ex);
            }
        }
        if (props.selected && props.selected.length > 0) {
            e.component.selectRows(props.selected, false);
            e.component.navigateToRow(props.selected[0]);
        }
    }, [props.tableFilters, props.tableSortOptions, props.selected]);
    const elementAttr = useMemo(() => ({ class: join(values(props.settings), " ") }), [props.settings]);
    if (!props.documentId || !columns || !gridData)
        return null;
    return (React.createElement(DataGridBase, { gridRef: gridRef, key: tableId, data: gridData, className: props.className, keyExpr: "Id", minBottomOffset: 0, columnResizingMode: "widget", minRowHeight: props.minRowHeight || 61, highlightUpdates: false, cellTemplateRequired: false, clearSelectionAfterUpdate: false, onSetSelected: props.onChangeSelected ? handleSetSelected : undefined, elementAttr: elementAttr, onCellPrepared: handleCellPrepared, onOptionChanged: handleOptionChanged, onFilterValueChange: handleFilterValueChange, onInitialized: handleInitialized, wrapperHeight: isString(props.height)
            ? props.height
            : Math.max((props.height || 0) - SIZES.DOCUMENT_OBJECT_TYPES_PANEL_HEIGHT, 0), filterBuilderFields: filterBuilderFields, showBorders: props.showBorders }, columns));
}
TableInner.defaultProps = {
    minRowHeight: 61,
    height: 0,
    imageWidgetSize: 40
};
export default memo(TableInner);
